import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, formatApiError } from "../contexts/AuthContext";
import API_BASE_URL from "../config";
import { Compass, Eye, EyeOff } from "lucide-react";
import { GoogleLogin } from "@react-oauth/google";

export default function Login() {
  const { loginWithGoogle, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname || "/";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password })
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || "Login failed. Check CORS or API URL.");
      }
      const data = await response.json();
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      await checkAuth();
      if (data?.user?.needs_subscription) {
        navigate("/subscribe");
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100dvh-64px)] flex overflow-x-hidden bg-himalayan-mist" data-testid="login-page">

      {/* Left image — desktop only */}
      <div className="hidden lg:block lg:w-1/2 relative shrink-0">
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-10 flex flex-col justify-end p-16">
          <h2 className="text-5xl font-display font-bold text-white mb-4 tracking-tight drop-shadow-md">
            Welcome back to the Himalayas
          </h2>
          <p className="text-white/90 text-xl font-medium max-w-md drop-shadow-md">
            Continue your journey. Connect, explore, and share your Uttarakhand stories.
          </p>
        </div>
        <img
          src="https://images.unsplash.com/photo-1542721847-51ed6bd083ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHx1dHRhcmFraGFuZCUyMGhpbWFsYXlhcyUyMGxhbmRzY2FwZXxlbnwwfHx8fDE3NzUyOTEwNzR8MA&ixlib=rb-4.1.0&q=85"
          alt="Uttarakhand Mountains"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-start sm:items-center justify-center p-6 sm:p-12 overflow-y-auto">
        <div className="w-full max-w-[440px] py-6 sm:py-0 animate-in fade-in slide-in-from-bottom-8">

          {/* Logo */}
          <div className="flex items-center gap-3 mb-10">
            <div className="w-12 h-12 rounded-[16px] flex items-center justify-center shrink-0 bg-forest-green shadow-warm">
              <Compass className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-bold text-3xl text-text-dark tracking-tight">
              Uttarakhand<span className="text-forest-green">Travel</span>
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-display font-bold text-text-dark tracking-tight mb-2">
            Sign in
          </h1>
          <p className="text-text-secondary-dark mb-8 text-base font-medium">
            Don't have an account?{" "}
            <Link to="/register" className="font-bold hover:underline transition-colors text-forest-green" data-testid="go-to-register">
              Create one
            </Link>
          </p>

          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-100 text-sm font-medium text-red-600 break-words flex items-center gap-2" data-testid="login-error">
              <div className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-sm font-bold text-text-secondary-dark mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full border-2 border-card-border rounded-[16px] px-4 py-3.5 bg-white text-text-dark placeholder-text-muted-dark outline-none transition-all text-base font-medium focus:border-forest-green focus:shadow-luxury"
                placeholder="you@example.com"
                data-testid="login-email"
                autoComplete="email"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-text-secondary-dark mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full border-2 border-card-border rounded-[16px] px-4 py-3.5 bg-white text-text-dark placeholder-text-muted-dark outline-none transition-all pr-12 text-base font-medium focus:border-forest-green focus:shadow-luxury"
                  placeholder="Enter password"
                  data-testid="login-password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted-dark hover:text-forest-green transition-colors p-1"
                  data-testid="toggle-password-visibility"
                >
                  {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full text-white bg-forest-green rounded-[16px] py-4 font-bold hover:-translate-y-1 hover:shadow-luxury-hover transition-all shadow-luxury disabled:opacity-50 disabled:hover:translate-y-0 text-lg mt-4"
              data-testid="login-submit"
            >
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>

          {/* Divider */}
          <div className="mt-8 mb-6 flex items-center gap-4">
            <div className="h-[2px] bg-card-border flex-1 rounded-full" />
            <span className="text-sm font-bold text-text-muted-dark shrink-0">OR CONTINUE WITH</span>
            <div className="h-[2px] bg-card-border flex-1 rounded-full" />
          </div>

          {/* Google button */}
          <div className="w-full overflow-hidden flex justify-center">
            <div className="w-full">
              <GoogleLogin
                onSuccess={async (credentialResponse) => {
                  setError("");
                  setLoading(true);
                  try {
                    const data = await loginWithGoogle(credentialResponse.credential, navigate);
                    if (!data?.user?.needs_subscription) {
                      navigate(from, { replace: true });
                    }
                  } catch (err) {
                    setError(formatApiError(err.response?.data?.detail) || "Google login failed");
                  } finally {
                    setLoading(false);
                  }
                }}
                onError={() => setError("Google login failed")}
                theme="outline"
                size="large"
                shape="rectangular"
                width="100%"
                logo_alignment="center"
                use_fedcm_for_prompt={true}
              />
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
